// Dial pad number parsing (SPEC §19, 2026-10-01). Pure. Accepts typing, keypad clicks and pastes in any format —
// "(404) 555-0123", "404.555.0123", "+1 404 555 0123", "1-404-555-0123", or text with a number inside — and turns
// them into national digits, a live display and E.164. The server normalises again (src/worker/lib/phone.ts).
import { areaCodeRegion } from '../../../src/shared/area-codes';

export interface PadNumber {
  trunk: boolean;        // a leading 1 / +1 was given (shown as "+1 ")
  digits: string;        // national digits so far, at most 10
  e164: string | null;   // set only for a complete, valid US/Canadian number
  display: string;       // what the input shows: '+1 (404) 555-01'
  error: string | null;  // plain words when it can't become a US/Canadian number
}

const EMPTY: PadNumber = { trunk: false, digits: '', e164: null, display: '', error: null };
const PHONE_CHARS = /^[\d\s().+\-]*$/;
// A 10-digit NANP number anywhere in text, optionally after +1 / 1, with ( ) . - or spaces between the groups.
const EMBEDDED = /(?<![\d+])(\+?\s*1[\s.\-]*)?\(?\s*(\d{3})\s*\)?[\s.\-]*(\d{3})[\s.\-]*(\d{4})(?!\d)/;
const EXTENSION = /\s*(?:ext\.?|extension|x|#)\s*\d{1,6}\s*$/i;

/** '4045550' -> '(404) 555-0'. */
export function formatPartial(digits: string): string {
  const d = digits.slice(0, 10);
  if (!d) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function problem(d: string): string | null {
  if (/^[01]/.test(d)) return "Area codes never start with 0 or 1. Check the first digit.";
  if (d.length >= 3 && /^[2-9]11/.test(d)) return `${d.slice(0, 3)} is a service code, not an area code.`;
  if (d.length >= 4 && /^[01]/.test(d.slice(3))) return 'The 4th digit can\'t be 0 or 1 (local exchanges start with 2-9).';
  return null;
}

function build(trunk: boolean, digits: string, error: string | null = null): PadNumber {
  const err = error ?? problem(digits);
  const complete = digits.length === 10 && !err;
  return {
    trunk, digits, error: err,
    e164: complete ? `+1${digits}` : null,
    display: `${trunk ? '+1 ' : ''}${formatPartial(digits)}`.trim(),
  };
}

/** Parse whatever is in the box (typed, clicked or pasted). */
export function parsePad(raw: string): PadNumber {
  const s = (raw ?? '').trim().replace(EXTENSION, '');
  if (!s) return EMPTY;
  const m = EMBEDDED.exec(s);
  if (m) return build(!!m[1], m[2] + m[3] + m[4]);
  if (!PHONE_CHARS.test(s)) {
    return { ...EMPTY, error: /\d/.test(s) ? 'No full 10-digit US or Canadian number in that text.' : 'No phone number in that text.' };
  }
  let all = s.replace(/\D/g, '');
  const trunk = /^\+/.test(s) || all.startsWith('1');
  if (/^\+/.test(s) && !all.startsWith('1') && all) return { ...EMPTY, error: 'Only US and Canadian numbers (+1) can be dialled.' };
  if (all.startsWith('1')) all = all.slice(1);
  if (all.length > 10) return { ...build(trunk, all.slice(0, 10)), e164: null, error: "That's more than 10 digits. A US or Canadian number is the area code plus 7 digits." };
  return build(trunk, all);
}

/** One keypad digit (or Backspace as '<') applied to the current number. */
export function pressKey(current: PadNumber, key: string): PadNumber {
  if (key === '<') {
    if (current.digits) return build(current.trunk, current.digits.slice(0, -1));
    return EMPTY;
  }
  if (!/^\d$/.test(key)) return current;
  if (!current.digits && !current.trunk && key === '1') return build(true, '');
  if (current.digits.length >= 10) return current;
  return build(current.trunk, current.digits + key);
}

/** Typing in the box. A delete that only removed punctuation ( ")", "-", " " ) removes the digit before it instead. */
export function typed(previous: PadNumber, value: string, deleting: boolean): { pad: PadNumber; value: string } {
  if (!PHONE_CHARS.test(value)) return { pad: parsePad(value), value };
  if (value.replace(/\D/g, '').replace(/^1/, '').length > 10) return { pad: previous, value: previous.display }; // an 11th digit is ignored
  let pad = parsePad(value);
  if (deleting && pad.digits === previous.digits && pad.trunk === previous.trunk && value.length < previous.display.length) {
    pad = pressKey(previous, '<');
  }
  if (pad.error && !pad.digits) return { pad, value };
  return { pad, value: pad.display };
}

// ---------------------------------------------------------------- what the pad shows next to the number

const TOLL_FREE = new Set(['800', '833', '844', '855', '866', '877', '888']);

export interface PadRegion {
  text: string;                    // 'Georgia', 'Nova Scotia / Prince Edward Island', 'Toll-free'
  country: 'US' | 'CA' | null;     // null: not a US/Canadian geographic code
  tone: 'ok' | 'warn' | 'bad';     // bad = the server will refuse it (excluded, toll-free, outside the US and Canada)
}

/** Where the area code is, from the same table the server uses (src/shared/area-codes.ts). Null under 3 digits. */
export function padRegion(digits: string): PadRegion | null {
  if (digits.length < 3 || /^[01]/.test(digits)) return null;
  const npa = digits.slice(0, 3);
  if (TOLL_FREE.has(npa)) return { text: "Toll-free: no state or province, so it can't be dialled from the pad", country: null, tone: 'bad' };
  const hit = areaCodeRegion(npa);
  if (!hit) return { text: `Area code ${npa} isn't a US or Canadian one we know`, country: null, tone: 'bad' };
  const excluded = hit.regions.some((r) => (hit.country === 'US' && r === 'TX') || (hit.country === 'CA' && r === 'QC'));
  const where = hit.names.join(' / ');
  if (excluded) return { text: `${where}: excluded, never called`, country: hit.country, tone: 'bad' };
  return { text: `${where}${hit.country === 'CA' ? ', Canada' : ''}`, country: hit.country, tone: hit.regions.length > 1 ? 'warn' : 'ok' };
}

export interface PadCampaign { id: number; name: string; country: 'US' | 'CA' | 'MIXED' }

/** The campaign the pad uses: the one asked for when it can call this country, else the first that can, else the one
 *  asked for (the server then explains the mismatch), else the first. */
export function pickCampaign(list: PadCampaign[], preferredId: number | null, country: 'US' | 'CA' | null): number | null {
  if (!list.length) return null;
  const fits = (c: PadCampaign) => !country || c.country === 'MIXED' || c.country === country;
  const preferred = list.find((c) => c.id === preferredId) ?? null;
  if (preferred && fits(preferred)) return preferred.id;
  const match = list.find(fits);
  return (match ?? preferred ?? list[0]).id;
}

// ---------------------------------------------------------------- "No campaign" and "Call from" (SPEC §19, 2026-10-02)

/** The pad's campaign choice: a campaign id, or 'none' (no campaign: no lead row, the same calling rules). */
export type PadChoice = number | 'none';

/**
 * Which campaign choice the pad shows. A pick made in this pad session wins; otherwise the setter's remembered last pick
 * if it was No campaign; otherwise today's default (the dialer's current campaign, else the setter's remembered one,
 * else the last campaign used here), switched to one that can call the number's country. No campaign when there is none.
 */
export function resolvePadChoice(list: PadCampaign[], o: {
  picked: PadChoice | null; stored: PadChoice | null; current: number | null; remembered: number | null; country: 'US' | 'CA' | null;
}): PadChoice {
  if (o.picked === 'none') return 'none';
  if (o.picked !== null && list.some((c) => c.id === o.picked)) return o.picked;
  if (o.picked === null && o.stored === 'none') return 'none';
  const storedId = typeof o.stored === 'number' ? o.stored : null;
  return pickCampaign(list, o.current ?? storedId ?? o.remembered, o.country) ?? 'none';
}

/** The remembered pad choice is per user (localStorage key). */
export const padChoiceKey = (userId: number): string => `vd.pad.campaign.${userId}`;

export function parsePadChoice(raw: string | null): PadChoice | null {
  if (raw === 'none') return 'none';
  const n = Number(raw);
  return raw && Number.isInteger(n) && n > 0 ? n : null;
}

export interface PadCallFrom {
  id: number; e164: string; country: 'US' | 'CA'; areaCode: string; region: string | null; metro: string | null;
  dialsLeft: number | null; unusable: string | null;   // dialsLeft null = not shown (admins' numbers hidden from this viewer, PJ 2026-10-07)
}

/** Why this number can't call the number on the pad (null = it can). `country` null = not known yet. */
export function callFromProblem(n: PadCallFrom, country: 'US' | 'CA' | null): string | null {
  if (n.unusable) return n.unusable;
  if (n.dialsLeft !== null && n.dialsLeft <= 0) return 'no dials left today';
  if (country && n.country !== country) return `a ${n.country === 'CA' ? 'Canadian' : 'US'} number can't call a ${country === 'CA' ? 'Canadian' : 'US'} one`;
  return null;
}

/** '(404) 555-0100 · Atlanta, GA · 37 dials left today'; with admins' numbers hidden from the viewer there is no count: '(404) 555-0100 · Atlanta, GA' */
export function callFromLabel(n: PadCallFrom): string {
  const e = n.e164;
  const num = /^\+1\d{10}$/.test(e) ? `(${e.slice(2, 5)}) ${e.slice(5, 8)}-${e.slice(8)}` : e;
  const place = n.metro ? `${n.metro}${n.region ? `, ${n.region}` : ''}` : n.region ?? `area code ${n.areaCode}`;
  const left = n.dialsLeft === null ? '' : ` · ${n.dialsLeft} dial${n.dialsLeft === 1 ? '' : 's'} left today`;
  return `${num} · ${place}${left}`;
}
